import { useState, useMemo } from 'react'
import { useParams } from 'react-router'
import { api } from '../api/client'
import { useEntities } from '../context/EntitiesContext'
import { PromptPageHeader } from '../components/PromptPageHeader'
import { PromptPageNavigation } from '../components/PromptPageNavigation'
import { extractErrorMessage } from '../utils/extractErrorMessage'
import { VariantEditor } from './prompt-detail/VariantEditor'
import { ScenarioPanel } from './prompt-detail/ScenarioPanel'
import type { IDecisionConfig } from 'extra/types/Decisions'

const DECISION_VARIANT_TEMPLATE: { config: IDecisionConfig, variablesNames: Array<string> } = {
  config: {
    _format: 'decision',
    provider: 'openai',
    model: 'gpt-6-luna',
    input: '',
    instructions: '{{item}}',
    choicesVariable: 'choices',
  },
  variablesNames: ['item'],
}
const EMPTY_VARIANT_TEMPLATE = { config: {}, variablesNames: [] }

export function PromptDetailPage () {
  const { id } = useParams<{ id: string }>()
  const { prompts, folders, variants: allVariants, deployments: allDeployments, loading: entitiesLoading, refreshVariants } = useEntities()

  const prompt = id ? prompts.find(p => p.id === id) || null : null
  const currentFolder = prompt?.folderId ? folders.find(f => f.id === prompt.folderId) : null

  const variants = useMemo(() => {
    if (!id) return []
    return allVariants
      .filter(v => v.promptId === id)
      .sort((a, b) => {
        const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0
        const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0
        return bTime - aTime
      })
  }, [allVariants, id])
  const deployments = useMemo(() => id ? allDeployments.filter(d => d.promptId === id) : [], [allDeployments, id])

  const [creatingVariant, setCreatingVariant] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  const selectedVariant = variants.length > 0 ? variants[0] : null
  const isDecision = prompt?.type === 'decision'

  const handleCreateVariant = async () => {
    if (!id) return

    setCreatingVariant(true)
    setCreateError(null)

    try {
      const baseName = selectedVariant ? `${selectedVariant.name} (copy)` : 'New Variant'
      let variantName = baseName
      let counter = 1
      while (variants.some(v => v.name === variantName)) {
        variantName = `${baseName} ${counter}`
        counter++
      }

      const template = isDecision ? DECISION_VARIANT_TEMPLATE : EMPTY_VARIANT_TEMPLATE
      await api.promptVariants.create({
        promptId: id,
        name: variantName,
        description: selectedVariant?.description || undefined,
        config: selectedVariant?.config || template.config,
        variablesNames: selectedVariant?.variablesNames || template.variablesNames,
        initialChatHistory: selectedVariant?.initialChatHistory || [],
      })
      await refreshVariants()
    } catch (err) {
      setCreateError(await extractErrorMessage(err, 'Failed to create variant'))
    } finally {
      setCreatingVariant(false)
    }
  }

  if (entitiesLoading) {
    return (
      <div>
        <div className="text-gray-400">Loading...</div>
      </div>
    )
  }

  if (!prompt) {
    return (
      <div>
        <div className="alert-error">Prompt not found</div>
      </div>
    )
  }

  return (
    <div>
      <PromptPageHeader prompt={prompt} currentFolder={currentFolder} />
      <PromptPageNavigation promptId={id!} />

      {createError && <div className="alert-error mb-4">{createError}</div>}

      <div className="grid grid-cols-2 gap-6">
        <div>
          <VariantEditor
            variants={variants}
            deployments={deployments}
            prompt={prompt}
            onCreateVariant={handleCreateVariant}
            creatingVariant={creatingVariant}
          />
        </div>
        {isDecision
          ? (
          <div className="text-gray-400 text-sm">
            Scenarios are not available for decision prompts. Call <code className="font-mono">/execute/decisions</code> with a deployment to try a variant.
          </div>
            )
          : (
          <ScenarioPanel
            promptId={id!}
            selectedVariant={selectedVariant}
          />
            )}
      </div>
    </div>
  )
}
